// src/components/blog/BlogSidebar.tsx
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ArticleMetadata } from '@dopomogai/types';
import clsx from 'clsx';
import { Search, Rss } from 'lucide-react';

interface BlogSidebarProps {
  categories: string[];
  selectedCategory: string;
  onSelectCategory: (category: string) => void;
  searchTerm: string;
  onSearchChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  recentPosts: Pick<ArticleMetadata, 'slug' | 'title'>[];
}

export const BlogSidebar: React.FC<BlogSidebarProps> = ({
  categories,
  selectedCategory,
  onSelectCategory,
  searchTerm,
  onSearchChange,
  recentPosts,
}) => {
  const { t } = useTranslation('blog');

  return (
    <>
      {/* Search Widget */}
      <div className="bg-white p-6 rounded-xl border-2 border-black">
        <div className="relative">
          <label htmlFor="blog-search" className="sr-only">
            {t('filters.searchPlaceholder')}
          </label>
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={20} />
          <input
            id="blog-search"
            type="text"
            placeholder={t('filters.searchPlaceholder')}
            value={searchTerm}
            onChange={onSearchChange}
            className="w-full pl-10 pr-4 py-2 border-2 border-slate-300 rounded-lg focus:ring-emerald-500 focus:border-emerald-500 transition"
          />
        </div>
      </div>

      {/* Categories Widget */}
      <div className="bg-white p-6 rounded-xl border-2 border-black">
        <h3 className="text-lg font-bold text-gray-900 mb-4">{t('filters.categoriesTitle', 'Categories')}</h3>
        <div className="flex flex-wrap gap-2">
          {categories.map(category => (
            <button
              key={category}
              onClick={() => onSelectCategory(category)}
              className={clsx(
                'px-3 py-1.5 text-sm font-semibold rounded-lg border-2 transition-colors duration-150',
                selectedCategory === category
                  ? 'bg-emerald-500 text-white border-emerald-600'
                  : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-100 hover:border-slate-400'
              )}
            >
              {category === 'All' ? t('filters.allCategories') : category}
            </button>
          ))}
        </div>
      </div>

      {/* Recent Posts Widget */}
      {recentPosts && recentPosts.length > 0 && (
        <div className="bg-white p-6 rounded-xl border-2 border-black">
          <h3 className="text-lg font-bold text-gray-900 mb-4">{t('sidebar.recentPostsTitle', 'Recent Posts')}</h3>
          <ul className="space-y-3">
            {recentPosts.map(post => (
              <li key={post.slug}>
                <Link
                  to={`/blog/${post.slug}`}
                  className="font-medium text-slate-600 hover:text-emerald-600 hover:underline transition-colors"
                >
                  {post.title}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Newsletter/CTA Widget */}
      <div className="bg-gradient-to-br from-emerald-50 to-teal-50 p-6 rounded-xl border-2 border-black text-center">
         <div className="mx-auto bg-emerald-100 w-12 h-12 rounded-lg flex items-center justify-center border-2 border-black mb-4">
            <Rss className="text-emerald-700" size={24} />
         </div>
        <h3 className="text-lg font-bold text-gray-900">{t('sidebar.subscribeTitle', 'Subscribe to Our Newsletter')}</h3>
        <p className="text-slate-600 text-sm mt-1 mb-4">{t('sidebar.subscribeSubtitle', 'Get the latest insights and updates directly in your inbox.')}</p>
        <form className="flex flex-col sm:flex-row gap-2">
            <input type="email" placeholder={t('sidebar.emailPlaceholder', 'your@email.com')} className="flex-grow w-full px-4 py-2 border-2 border-slate-300 rounded-lg focus:ring-emerald-500 focus:border-emerald-500 transition"/>
            <button type="submit" className="px-4 py-2 bg-emerald-500 text-white font-semibold rounded-lg border-2 border-black hover:bg-emerald-600 transition-colors">
                {t('sidebar.subscribeCta', 'Subscribe')}
            </button>
        </form>
      </div>
    </>
  );
};